import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Copy, ExternalLink, Trash2 } from "lucide-react";
import { api, eur, STATUS_LABEL, errMsg } from "@/lib/api";

const STATUSES = ["in_lavorazione", "spedito", "consegnato", "annullato"];
const AMZ_COLOR = { da_ordinare: "#FF3333", in_elaborazione: "#FF9900", errore: "#FF3333", ordinato: "#FF9900", spedito: "#0033FF", consegnato: "#00A651", annullato: "#FF3333", in_attesa: "#555" };
const AMZ_LABEL = { da_ordinare: "Da ordinare su Amazon", in_elaborazione: "In elaborazione", errore: "Errore", ordinato: "Ordinato", spedito: "Spedito", consegnato: "Consegnato", annullato: "Annullato", in_attesa: "In attesa" };

const ManualForm = ({ o, onSaved }) => {
  const [f, setF] = useState({ amazon_order_id: o.amazon_order_id || "", tracking_number: o.tracking_number || "", tracking_url: o.tracking_url || "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/orders/${o.id}/amazon`, { ...f, tracking_number: f.tracking_number || null, tracking_url: f.tracking_url || null });
      toast.success("Dati ordine Amazon salvati");
      onSaved();
    } catch (er) { toast.error(errMsg(er)); }
  };
  const inp = (k, ph, req) => (
    <input data-testid={`manual-${k}-${o.id}`} required={req} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className="field !py-1.5 text-sm" />
  );
  return (
    <form onSubmit={save} className="space-y-2 md:col-span-2 border-t border-[#0F0F0F]/15 pt-4">
      <span className="tag text-[#555]">Dopo l'acquisto su Amazon, inserisci qui i dati</span>
      <div className="grid md:grid-cols-4 gap-2">
        {inp("amazon_order_id", "N° ordine Amazon (es. 405-1234567-1234567)", true)}
        {inp("tracking_number", "N° tracking (facoltativo)")}
        {inp("tracking_url", "Link tracking https:// (facoltativo)")}
        <button data-testid={`manual-save-${o.id}`} className="btn-primary text-sm min-h-[38px]">Salva</button>
      </div>
      <p className="text-xs text-[#555]">Quando il pacco parte, imposta lo stato su "Spedito": il cliente riceve l'email con il tracking.</p>
    </form>
  );
};

const copyAddress = (c) => {
  navigator.clipboard.writeText(`${c.name}\n${c.address}\n${c.zip} ${c.city}\n${c.country}${c.phone ? `\nTel. ${c.phone}` : ""}`);
  toast.success("Indirizzo copiato");
};

const Details = ({ o, onSaved }) => (
  <tr className="bg-[#FBFBFB]" data-testid={`order-details-${o.id}`}>
    <td colSpan={8} className="px-4 py-4">
      <div className="grid md:grid-cols-2 gap-6">
      <div>
        <span className="tag text-[#555]">Consegna diretta Amazon a</span>
        <p className="mt-1">{o.customer.name} · {o.customer.email} {o.customer.phone && `· ${o.customer.phone}`}</p>
        <p>{o.customer.address}, {o.customer.zip} {o.customer.city} ({o.customer.country})</p>
        <button data-testid={`copy-address-${o.id}`} onClick={() => copyAddress(o.customer)} className="btn-ghost tag px-2 py-1 mt-2 flex items-center gap-1"><Copy size={12} /> Copia indirizzo</button>
      </div>
      <div className="space-y-1">
        <span className="tag text-[#555]">Articoli</span>
        {o.items.map((i, k) => (
          <p key={k} className="font-mono text-xs flex items-center gap-2 flex-wrap">
            <a href={`https://www.amazon.it/dp/${i.asin}`} target="_blank" rel="noreferrer" data-testid={`amazon-link-${o.id}-${k}`}
              className="inline-flex items-center gap-1 text-[#0033FF] underline">{i.asin} <ExternalLink size={11} /></a>
            × {i.qty} — Amazon {eur(i.amazon_unit_cost)} → Cliente {eur(i.unit_price)}
          </p>
        ))}
      </div>
      {o.payment_status === "pagato" && o.amazon_provider !== "zinc" && <ManualForm o={o} onSaved={onSaved} />}
      </div>
    </td>
  </tr>
);

export default function AdminOrders() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/orders").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}/status`, { status });
    toast.success("Stato aggiornato");
    load();
  };

  const del = async (o) => {
    if (!window.confirm(`Eliminare definitivamente l'ordine ${o.code}?`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      toast.success("Ordine eliminato");
    } catch (er) { toast.error(errMsg(er)); }
    load();
  };

  const retry = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/amazon-retry`);
      toast.success("Ordine Amazon reinviato");
    } catch (er) { toast.error(errMsg(er)); }
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div>
        <span className="tag text-[#0033FF]">Vendite</span>
        <h1 className="font-display font-black text-4xl tracking-tight">Ordini</h1>
        <p className="text-sm text-[#555] mt-1">Evasione <b>manuale</b>: per ogni ordine pagato ricevi un'email. Apri l'ordine, compra su Amazon con l'indirizzo del cliente e salva il numero d'ordine Amazon. Il cliente riceve un'email a ogni cambio di stato.</p>
        {list.some((o) => o.amazon_status === "da_ordinare" && o.payment_status === "pagato") && (
          <p className="mt-3 inline-block bg-[#FF3333] text-white tag px-3 py-1.5" data-testid="orders-to-fulfil-count">
            {list.filter((o) => o.amazon_status === "da_ordinare" && o.payment_status === "pagato").length} ordini da evadere su Amazon
          </p>
        )}
      </div>
      <div className="border border-[#0F0F0F] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F0F] text-white">
            <tr>{["", "Codice", "Cliente", "Totale", "Costo Amazon", "Margine", "Ordine Amazon", "Stato"].map((h, i) => <th key={i} className="tag text-left px-3 py-2 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={8} className="p-4 text-[#555]" data-testid="orders-empty">Nessun ordine.</td></tr>}
            {list.map((o) => (
              <Fragment key={o.id}>
                <tr className="border-t border-[#0F0F0F]/15" data-testid={`admin-order-row-${o.id}`}>
                  <td className="px-3 py-2">
                    <button data-testid={`toggle-order-${o.id}`} onClick={() => setOpen(open === o.id ? null : o.id)} className="p-1 hover:text-[#0033FF]">
                      <ChevronDown size={16} className={open === o.id ? "rotate-180" : ""} />
                    </button>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{o.code}<br /><span className="text-[#555]">{new Date(o.created_at).toLocaleString("it-IT")}</span></td>
                  <td className="px-3 py-2">{o.customer.name}<br /><span className="text-xs text-[#555]">{o.customer.city}</span></td>
                  <td className="px-3 py-2 font-mono font-semibold">{eur(o.total)}<br /><span className={`tag ${o.payment_status === "pagato" ? "text-[#00A651]" : "text-[#FF9900]"}`} data-testid={`payment-status-${o.id}`}>{o.payment_status}</span></td>
                  <td className="px-3 py-2 font-mono">{eur(o.amazon_cost)}</td>
                  <td className="px-3 py-2 font-mono text-[#0033FF]">{eur(o.margin)}</td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-xs block">{o.amazon_order_id}</span>
                    <span className="tag" style={{ color: AMZ_COLOR[o.amazon_status] }} data-testid={`amazon-status-${o.id}`}>{AMZ_LABEL[o.amazon_status] || o.amazon_status}</span>
                    {o.amazon_provider === "mock" && <span className="tag text-[#555] block">simulato</span>}
                    {o.amazon_provider === "manuale" && <span className="tag text-[#555] block">manuale</span>}
                    {o.tracking_number && <span className="font-mono text-xs block">Tracking {o.tracking_number}</span>}
                    {o.amazon_status === "errore" && (
                      <>
                        <span className="text-xs text-[#FF3333] block max-w-[180px]">{o.amazon_error}</span>
                        <button data-testid={`amazon-retry-${o.id}`} onClick={() => retry(o.id)} className="btn-ghost tag px-2 py-1 mt-1">Riprova</button>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {o.payment_status !== "pagato" ? <span className="tag text-[#555]">{STATUS_LABEL[o.status]}</span> : (
                    <select data-testid={`order-status-select-${o.id}`} value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="field !py-1.5 !text-xs">
                      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>)}
                    <button data-testid={`delete-order-${o.id}`} onClick={() => del(o)} title="Elimina ordine" className="p-1 mt-1 hover:text-[#FF3333]"><Trash2 size={14} /></button>
                  </td>
                </tr>
                {open === o.id && <Details o={o} onSaved={load} />}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
